import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import { 
  CardContent, 
  Typography, 
  Box,
  Button,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';

const AccountLanguage = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';

  // Récupération des informations de l'utilisateur
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const response = await axios.get('/api/user', config);
        const currentUser = response.data.find(u => u.matricule === user);
        
        if (currentUser) {
          setUserInfo(currentUser);
          setSelectedLanguage(currentUser.language || 'fr');
        }
      } catch (err) {
        setError('Erreur lors de la récupération des informations utilisateur');
        console.error(err);
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = sessionStorage.getItem('authToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `/api/user/${user}/language`,
        { language: selectedLanguage },
        config
      );

      setSuccess('Langue mise à jour avec succès !');
    } catch (err) {
      setError('Erreur lors de la mise à jour de la langue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour modifier votre langue.</Alert>
      </Box>
    );
  }

  if (!userInfo) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Box display="flex" justifyContent="center" m={2}>
          <CircularProgress sx={{ color: isDark ? '#90caf9' : 'inherit' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ color: isDark ? '#90caf9' : 'inherit' }}
        >
          Langue de l'interface
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            gutterBottom
            sx={{ color: isDark ? '#b0bec5' : 'inherit' }}
          >
            Sélectionnez la langue avec laquelle vous rédigez vos documents :
          </Typography>
          
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              <FormControlLabel 
                value="fr" 
                control={
                  <Radio 
                    sx={{
                      color: isDark ? '#90caf9' : 'inherit',
                      '&.Mui-checked': {
                        color: isDark ? '#90caf9' : 'inherit',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: isDark ? '#fff' : 'inherit' }}>
                    Français
                  </Typography>
                }
              />
              <FormControlLabel 
                value="nl" 
                control={
                  <Radio 
                    sx={{
                      color: isDark ? '#90caf9' : 'inherit',
                      '&.Mui-checked': {
                        color: isDark ? '#90caf9' : 'inherit',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: isDark ? '#fff' : 'inherit' }}>
                    Nederlands
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || selectedLanguage === userInfo.language}
            color="primary"
            fullWidth
            sx={{
              mt: 3,
              bgcolor: isDark ? '#90caf9' : 'inherit',
              color: isDark ? '#1a253c' : 'inherit',
              '&:hover': {
                bgcolor: isDark ? '#64b5f6' : 'inherit'
              },
              '&:disabled': {
                bgcolor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit',
                color: isDark ? 'rgba(26, 37, 60, 0.6)' : 'inherit'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: isDark ? '#1a253c' : 'inherit' }} /> : 'Sauvegarder'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </CardContent>
    </Box>
  );
};

export default AccountLanguage;
